import React, { useState, useEffect } from 'react';
import { Database, Upload, Search, Link2, Send, Trash2, RefreshCw, Check,
         AlertTriangle, X, ChevronRight, Clock } from 'lucide-react';
import { useRafraichissementAuto } from '../rafraichissementAuto';
import { useBlocageDefilement } from '../blocageDefilement';
import { copierTexte } from '../pressePapier';

/* ---------------------------------------------------------------------------
   Onglet "Data ES-PROTECT" : le fichier de prospects, et le lien d'ouverture
   de dossier deja rempli qu'on leur envoie.

   Le tableau tient dans la largeur de l'ecran, sans defilement horizontal :
   il ne montre que ce qui sert a reconnaitre et a trier quelqu'un. Tout le
   reste du fichier est dans la fiche, qui s'ouvre au clic sur la ligne. Les
   boutons d'action sont dans la derniere colonne, calee a droite.
   --------------------------------------------------------------------------- */

const API = process.env.REACT_APP_API_URL || '/api';

/* Etat de la fenetre de 12 heures pendant laquelle le tarif reserve s'applique.
   Recalcule a partir de la date de fin, jamais decremente : une fiche laissee
   ouverte doit afficher le vrai temps restant, pas celui de son ouverture. */
const FENETRE_HEURES = 12;

/* Fait avancer l'affichage du temps restant. Sans lui, la valeur n'est
   recalculee qu'au prochain rendu : une fiche ouverte afficherait indefiniment
   le temps qu'il restait a son ouverture. */
const useBattement = (millisecondes = 30000) => {
  const [, battre] = useState(0);
  useEffect(() => {
    const t = setInterval(() => battre((n) => n + 1), millisecondes);
    return () => clearInterval(t);
  }, [millisecondes]);
};
/* Deux sources pour la meme date : le suivi ES-PROTECT, qui fait foi au
   chargement de la fiche, et la copie du CRM, mise a jour des qu'on agit. Une
   fenetre ne fait que se repousser : la date la plus lointaine est la bonne. */
const finLaPlusLointaine = (...dates) => dates
  .filter(Boolean)
  .sort((a, b) => Date.parse(b) - Date.parse(a))[0] || null;

const etatFenetre = (fin) => {
  if (!fin) return { connue: false, ouverte: false, texte: 'pas encore ouverte' };
  const minutes = Math.round((Date.parse(fin) - Date.now()) / 60000);
  if (!(minutes > 0)) return { connue: true, ouverte: false, texte: 'fenêtre fermée' };
  return { connue: true, ouverte: true, minutes,
           texte: `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min` };
};
const jetonEntete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const dateHeure = (v) => (v ? new Date(String(v).replace(' ', 'T')).toLocaleString('fr-FR',
  { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', hour: '2-digit',
    minute: '2-digit' }) : '—');
const nombre = (v) => Number(v || 0).toLocaleString('fr-FR');

const STATUTS = {
  a_contacter: { texte: 'À contacter', fond: '#eef1f8', couleur: '#455a64' },
  a_rappeler:  { texte: 'À rappeler', fond: '#fff1e0', couleur: '#b45309' },
  lien_envoye: { texte: 'Lien envoyé', fond: '#e8f1ff', couleur: '#14448f' },
  inscrit:     { texte: 'Inscrit', fond: '#e8f5e9', couleur: '#1b5e20' },
  ecarte:      { texte: 'Écarté', fond: '#f5f5f5', couleur: '#78909c' }
};

const champ = { padding: '9px 12px', border: '1px solid #dfe3ea', borderRadius: 6,
                fontSize: 13.5, color: '#263238' };

/* Lecture d'un CSV (ou separe par des points-virgules ou des tabulations),
   guillemets compris : c'est ce que sortent Excel et Google Sheets. */
function lireCsv(texte) {
  const sep = (() => {
    const tete = texte.split(/\r?\n/)[0] || '';
    const compte = (c) => (tete.match(new RegExp(`\\${c}`, 'g')) || []).length;
    return [';', ',', '\t'].sort((a, b) => compte(b) - compte(a))[0];
  })();
  const lignes = [];
  let valeur = '', ligne = [], dansGuillemets = false;
  for (let i = 0; i < texte.length; i++) {
    const c = texte[i];
    if (dansGuillemets) {
      if (c === '"' && texte[i + 1] === '"') { valeur += '"'; i++; }
      else if (c === '"') dansGuillemets = false;
      else valeur += c;
    } else if (c === '"') dansGuillemets = true;
    else if (c === sep) { ligne.push(valeur); valeur = ''; }
    else if (c === '\n') { ligne.push(valeur); lignes.push(ligne); ligne = []; valeur = ''; }
    else if (c !== '\r') valeur += c;
  }
  if (valeur || ligne.length) { ligne.push(valeur); lignes.push(ligne); }
  if (!lignes.length) return [];
  const entetes = lignes.shift().map((h, i) => (h || '').trim() || `colonne_${i + 1}`);
  return lignes
    .filter((l) => l.some((v) => (v || '').trim()))
    .map((l) => Object.fromEntries(entetes.map((h, i) => [h, (l[i] || '').trim()])));
}

/* ─────────────────────────── fiche d'un prospect ───────────────────────────
   Une seule liste : chaque information apparait une fois. Les colonnes du
   fichier d'origine sont toutes reprises, y compris celles qu'on n'exploite
   pas, avec un libelle lisible. Quand une valeur a ete retraitee (telephone
   remis a dix chiffres, date de naissance interpretee), on montre la valeur
   utilisable ET celle du fichier, parce que les deux servent : l'une pour
   appeler, l'autre pour retrouver la ligne dans le fichier d'origine. */
const LIBELLES = {
  PRENOM: 'Prénom', Prenom: 'Prénom',
  NOM: 'Nom', Nom: 'Nom',
  EMAIL: 'Adresse email', Email: 'Adresse email',
  MOBILE: 'Téléphone', Mobile: 'Téléphone',
  ADRESSE: 'Adresse', Adresse: 'Adresse',
  VILLE: 'Ville', Ville: 'Ville',
  CP: 'Code postal',
  'date de naissance': 'Date de naissance', DOB: 'Date de naissance',
  'Date de naissance': 'Date de naissance',
  'IBAN/RIB': 'IBAN / RIB',
  compagnie: 'Compagnie', Compagnie: 'Compagnie',
  FICHIER: 'Fichier d’origine'
};
const ORDRE = ['Prénom', 'Nom', 'Adresse email', 'Téléphone', 'Adresse', 'Code postal', 'Ville',
               'Date de naissance', 'IBAN / RIB', 'Compagnie', 'Fichier d’origine'];

/* Regroupement par usage : on lit une fiche pour agir, pas pour admirer une liste. */
const GROUPES = [
  { titre: 'Qui est cette personne', champs: ['Prénom', 'Nom', 'Date de naissance'] },
  { titre: 'Comment la joindre',
    champs: ['Adresse email', 'Téléphone', 'Adresse', 'Code postal', 'Ville'] },
  { titre: 'Ses contrats connus', champs: ['IBAN / RIB', 'Compagnie'] },
  { titre: 'Provenance', champs: ['Fichier d’origine'] }
];

const TONS = {
  ok:       { fond: '#e8f5e9', bord: '#c8e6c9', couleur: '#1b5e20' },
  attente:  { fond: '#fff8e1', bord: '#ffe0b2', couleur: '#8d6e00' },
  refus:    { fond: '#ffebee', bord: '#ffcdd2', couleur: '#b71c1c' },
  incident: { fond: '#f3e8ff', bord: '#e1ccf7', couleur: '#6b21a8' },
  neutre:   { fond: '#f5f7fa', bord: '#e6eaf0', couleur: '#546e7a' }
};

function Fiche({ ligne, onFermer, onAction, enCours }) {
  const st = STATUTS[ligne.statut] || STATUTS.a_contacter;
  useBattement();                       /* le temps restant avance a l'ecran */
  const [suivi, setSuivi] = useState(null);
  const [suiviErreur, setSuiviErreur] = useState(null);

  /* l'etat du paiement se lit directement ici : c'est la meme information que
     dans Paiements en direct, mais rapportee a la personne qu'on regarde */
  useEffect(() => {
    let vivant = true;
    if (!ligne.jeton) { setSuivi(null); return undefined; }
    (async () => {
      try {
        const r = await fetch(`${API}/emandat/invitation/${ligne.jeton}`, { headers: jetonEntete() });
        const j = await r.json();
        if (!vivant) return;
        if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
        setSuivi(j); setSuiviErreur(null);
      } catch (e) { if (vivant) setSuiviErreur(e.message); }
    })();
    return () => { vivant = false; };
  }, [ligne.jeton]);

  /* on regroupe par libelle pour ne jamais afficher deux fois la meme chose */
  const champs = new Map();
  Object.entries(ligne.donnees || {}).forEach(([cle, valeur]) => {
    if (valeur === null || valeur === undefined || valeur === '') return;
    const libelle = LIBELLES[cle] || cle;
    if (!champs.has(libelle)) champs.set(libelle, { valeur: String(valeur) });
  });

  /* les valeurs retraitees, quand elles apportent vraiment quelque chose */
  const poser = (libelle, valeur, note) => {
    if (!valeur) return;
    const deja = champs.get(libelle);
    if (deja && String(deja.valeur) === String(valeur)) return;
    champs.set(libelle, { valeur, note: deja ? `${note} ${deja.valeur}` : note });
  };
  poser('Téléphone', ligne.telephone, 'dans le fichier :');
  poser('Date de naissance', ligne.date_naissance, 'écrite dans le fichier :');
  if (!ligne.date_naissance && champs.has('Date de naissance')) {
    const d = champs.get('Date de naissance');
    champs.set('Date de naissance',
      { valeur: d.valeur, note: 'format ambigu dans le fichier, non repris dans le lien' });
  }
  if (!ligne.email) champs.set('Adresse email', { valeur: 'aucune', absent: true });

  const cles = [...ORDRE.filter((l) => champs.has(l)),
                ...[...champs.keys()].filter((l) => !ORDRE.includes(l))];

  return (
    <div onClick={onFermer}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,35,.55)', zIndex: 1000,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 10, width: 'min(760px,100%)',
                    boxShadow: '0 20px 60px rgba(10,15,30,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px',
                      borderBottom: '1px solid #eceff1' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#263238' }}>
              {[ligne.prenom, ligne.nom].filter(Boolean).join(' ') || 'Sans nom'}
            </h3>
            <span style={{ background: st.fond, color: st.couleur, borderRadius: 999,
                           padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{st.texte}</span>
            {ligne.lien_envoye_le && <span style={{ color: '#90a4ae', fontSize: 12.5, marginLeft: 8 }}>
              lien envoyé le {dateHeure(ligne.lien_envoye_le)}</span>}
          </div>
          <button onClick={onFermer} style={{ background: 'none', border: 'none', cursor: 'pointer',
                                              color: '#90a4ae' }}><X size={20} /></button>
        </div>

        <div style={{ padding: '14px 22px 20px' }}>
          {(() => {
            const rendus = new Set();
            const rendreChamp = (libelle) => {
              const c = champs.get(libelle);
              if (!c) return null;
              rendus.add(libelle);
              return (
                <div key={libelle} style={{ padding: '7px 0', borderBottom: '1px solid #f1f3f7' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                    <div style={{ width: 150, flex: 'none', color: '#546e7a', fontSize: 12.5,
                                  fontWeight: 600 }}>{libelle}</div>
                    <div style={{ fontSize: 14.5, wordBreak: 'break-word', flex: 1,
                                  color: c.absent ? '#b0bec5' : '#263238',
                                  fontFamily: libelle === 'IBAN / RIB'
                                    ? 'ui-monospace,Menlo,monospace' : 'inherit' }}>
                      {c.valeur}
                      {c.note && <span style={{ color: '#90a4ae', fontSize: 12, marginLeft: 8 }}>
                        ({c.note})</span>}
                    </div>
                  </div>
                </div>
              );
            };
            const blocs = GROUPES.map((g) => {
              const dedans = g.champs.filter((l) => champs.has(l));
              if (!dedans.length) return null;
              return (
                <div key={g.titre} style={{ marginBottom: 14 }}>
                  <h4 style={{ margin: '14px 0 4px', fontSize: 12, textTransform: 'uppercase',
                               letterSpacing: '.05em', color: '#667eea' }}>{g.titre}</h4>
                  {dedans.map(rendreChamp)}
                </div>
              );
            });
            /* toute colonne du fichier qu'on n'a pas prevue reste visible */
            const restants = cles.filter((l) => !rendus.has(l));
            return (
              <>
                {blocs}
                {restants.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ margin: '14px 0 4px', fontSize: 12, textTransform: 'uppercase',
                                 letterSpacing: '.05em', color: '#667eea' }}>
                      Autres colonnes du fichier</h4>
                    {restants.map(rendreChamp)}
                  </div>
                )}
              </>
            );
          })()}

          <h4 style={{ margin: '20px 0 6px', fontSize: 12.5, textTransform: 'uppercase',
                       letterSpacing: '.05em', color: '#667eea' }}>Où en est son paiement</h4>
          {!ligne.jeton ? (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#90a4ae' }}>
              Aucun lien ne lui a encore été envoyé.
            </p>
          ) : suiviErreur ? (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#b71c1c' }}>
              Suivi indisponible : {suiviErreur}
            </p>
          ) : !suivi ? (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#90a4ae' }}>lecture en cours…</p>
          ) : (() => {
            const ton = TONS[suivi.niveau] || TONS.neutre;
            return (
              <div style={{ background: ton.fond, border: `1px solid ${ton.bord}`,
                            borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                <b style={{ fontSize: 14, color: ton.couleur }}>{suivi.situation}</b>
                {suivi.conseil && (
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#546e7a' }}>{suivi.conseil}</p>
                )}
                {(suivi.paiements || []).map((p) => (
                  <div key={p.reference} style={{ marginTop: 10, paddingTop: 10,
                                                  borderTop: `1px solid ${ton.bord}`, fontSize: 12.5,
                                                  color: '#455a64' }}>
                    <b>{Number(p.montant).toLocaleString('fr-FR',
                      { style: 'currency', currency: 'EUR' })}</b>
                    {' · '}{p.statut === 'accepte' ? 'accepté'
                      : p.statut === 'refuse' ? 'refusé'
                      : p.statut === 'echec_technique' ? 'bloqué avant la carte' : 'en attente'}
                    {p.numero_auto ? ` · autorisation ${p.numero_auto}` : ''}
                    {p.code_retour ? ` · ${p.code_retour}` : ''}
                    <div style={{ color: '#90a4ae', fontSize: 11.5,
                                  fontFamily: 'ui-monospace,Menlo,monospace' }}>
                      {p.reference} · TPE {p.tpe} · {dateHeure(p.repondu_le || p.cree_le)}
                    </div>
                  </div>
                ))}
                {(suivi.parcours || []).length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12.5, color: '#3949ab' }}>
                      Voir ce qu’il a fait, étape par étape
                    </summary>
                    <div style={{ marginTop: 6 }}>
                      {suivi.parcours.map((e, i) => (
                        <div key={i} style={{ fontSize: 12.5, color: '#546e7a', padding: '3px 0' }}>
                          <span style={{ color: '#b0bec5' }}>{dateHeure(e.cree_le)}</span>
                          {' — '}{e.titre || e.etape}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })()}

          <h4 style={{ margin: '20px 0 6px', fontSize: 12.5, textTransform: 'uppercase',
                       letterSpacing: '.05em', color: '#667eea' }}>Son lien d’ouverture de dossier</h4>
          <p style={{ margin: '0 0 8px', fontSize: 12.5, color: '#78909c' }}>
            Ses informations sont déjà dedans : il n’a qu’à accepter le mandat et saisir sa carte.
          </p>
          <div style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 8,
                        padding: '12px 14px', fontSize: 15, fontWeight: 600,
                        fontFamily: 'ui-monospace,Menlo,monospace', color: '#000091' }}>
            {ligne.lien || 'préparation du lien…'}
          </div>

          {(() => {
            const f = etatFenetre(finLaPlusLointaine(
              suivi && suivi.invitation && suivi.invitation.offre_expire_le,
              ligne.offre_expire_le));
            return (
              <div style={{ marginTop: 12, padding: '11px 14px', borderRadius: 8,
                            background: f.ouverte ? '#f1f8f2' : '#fff8e6',
                            border: `1px solid ${f.ouverte ? '#c8e6c9' : '#ffe0a3'}`,
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Clock size={15} color={f.ouverte ? '#2e7d32' : '#b26a00'} />
                <span style={{ fontSize: 12.5, color: f.ouverte ? '#1b5e20' : '#7a4f00',
                               flex: 1, minWidth: 240, lineHeight: 1.55 }}>
                  <b>Tarif réservé à 89 €{f.ouverte ? ` : encore ${f.texte}` : ` : ${f.texte}`}.</b>
                  {' '}En renvoyant le lien ou en le copiant de nouveau, vous renouvelez le délai
                  de {FENETRE_HEURES} h de validité.
                </span>
                <button onClick={() => onAction('renouveler', ligne)}
                        disabled={!ligne.jeton || enCours === `renouv-${ligne.id}`}
                        style={{ ...champ, background: '#fff', cursor: ligne.jeton ? 'pointer' : 'not-allowed',
                                 display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                  <RefreshCw size={14} />
                  {enCours === `renouv-${ligne.id}` ? 'Renouvellement…' : `Renouveler les ${FENETRE_HEURES} h`}
                </button>
              </div>
            );
          })()}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <button onClick={() => onAction('envoyer', ligne)}
                    disabled={!ligne.email || enCours === `envoi-${ligne.id}`}
                    style={{ ...champ, border: 'none', background: ligne.email ? '#000091' : '#b0bec5',
                             color: '#fff', fontWeight: 600,
                             cursor: ligne.email ? 'pointer' : 'not-allowed',
                             display: 'flex', alignItems: 'center', gap: 6 }}>
              <Send size={14} /> {enCours === `envoi-${ligne.id}` ? 'Envoi…' : 'Envoyer le lien par email'}
            </button>
            <button onClick={() => onAction('copier', ligne)}
                    style={{ ...champ, background: '#fff', cursor: 'pointer',
                             display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link2 size={14} /> Copier le lien
            </button>
            {ligne.lien && (
              <a href={`${ligne.lien}?apercu=1`} target="_blank" rel="noreferrer"
                 style={{ ...champ, background: '#fff', cursor: 'pointer', textDecoration: 'none',
                          color: '#263238', display: 'flex', alignItems: 'center', gap: 6 }}>
                Voir ce qu’il verra (sans compter comme une ouverture) <ChevronRight size={14} />
              </a>
            )}
            <span style={{ flex: 1 }} />
            <select value={ligne.statut} onChange={(e) => onAction('statut', ligne, e.target.value)}
                    style={champ}>
              {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.texte}</option>)}
            </select>
            <button onClick={() => onAction('supprimer', ligne)}
                    style={{ ...champ, background: '#fff', border: '1px solid #f3d2d2',
                             color: '#b71c1c', cursor: 'pointer',
                             display: 'flex', alignItems: 'center', gap: 6 }}>
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────── l'onglet ──────────────────────────────── */
/* Origine des fichiers et envoi par lot.
   Un fichier sans fournisseur ni preuve du consentement ne peut rien envoyer :
   le serveur le refuse, ce panneau montre pourquoi et permet de le completer.
   Les clients existants n'en ont pas besoin. */
function OrigineFichiers({ jetonEntete, champ, onMessage, onEnvoye }) {
  const [infos, setInfos] = useState({ fichiers: [], opposition: 0 });
  const [edition, setEdition] = useState(null);
  const [lot, setLot] = useState({});
  const [occupe, setOccupe] = useState(null);

  const lire = async () => {
    try {
      const r = await fetch(`${API}/esprotect-data/fichiers`, { headers: jetonEntete() });
      const j = await r.json();
      if (r.ok) setInfos(j);
    } catch (e) { /* le tableau principal signale deja les pannes */ }
  };
  useEffect(() => { lire(); }, []);

  const enregistrer = async () => {
    setOccupe('origine');
    try {
      const r = await fetch(`${API}/esprotect-data/fichiers`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify(edition) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setEdition(null);
      onMessage({ type: 'ok', texte: 'Origine du fichier enregistrée.' });
      await lire();
    } catch (e) { onMessage({ type: 'ko', texte: e.message }); }
    setOccupe(null);
  };

  const envoyerLot = async (f) => {
    const nombre = parseInt(lot[f.fichier], 10) || 50;
    setOccupe(`lot-${f.fichier}`);
    try {
      const appel = async (confirmer) => {
        const r = await fetch(`${API}/esprotect-data/envoi-lot`, {
          method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ fichier: f.fichier, nombre, confirmer }) });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
        return j;
      };
      const a = await appel(false);
      if (!a.partiraient) {
        onMessage({ type: 'ko', texte: a.deja_aujourdhui >= a.plafond
          ? `Plafond du jour atteint (${a.plafond} envois). Reprenez demain.`
          : 'Aucune ligne envoyable dans ce fichier (déjà contactées, sans email ou opposées).' });
      } else if (window.confirm(`Envoyer le mail d’alerte à ${a.partiraient} personne(s) du fichier « ${f.fichier} » ?\n\nDéjà envoyés aujourd’hui : ${a.deja_aujourdhui} / ${a.plafond}.`)) {
        const j = await appel(true);
        onMessage({ type: j.echecs.length ? 'ko' : 'ok', fort: `${j.envoyes} mail(s) envoyé(s).`,
          texte: j.echecs.length ? `${j.echecs.length} échec(s) : ${j.echecs.slice(0, 3).map((x) => `${x.email} (${x.erreur})`).join(', ')}` : 'Les liens sont valables 12 heures.' });
        onEnvoye();
      }
    } catch (e) { onMessage({ type: 'ko', texte: e.message }); }
    setOccupe(null);
  };

  if (!infos.fichiers.length) return null;
  return (
    <div style={{ border: '1px solid #e3e7ef', borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
      <b style={{ fontSize: 13.5, color: '#263238' }}>Origine des fichiers et envoi</b>
      <span style={{ fontSize: 12, color: '#90a4ae' }}> · liste d’opposition : {infos.opposition} personne(s)</span>
      <p style={{ margin: '4px 0 10px', fontSize: 12.5, color: '#78909c' }}>
        Obligatoire pour écrire à des prospects : le fournisseur du fichier et la preuve qu’ils ont
        accepté de recevoir des offres de partenaires. Les personnes désinscrites ne sont plus jamais contactées.
      </p>
      {infos.fichiers.map((f) => (
        <div key={f.fichier || 'sans'} style={{ borderTop: '1px solid #f0f2f6', padding: '9px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
            <b style={{ color: '#263238' }}>{f.fichier || 'sans fichier'}</b>
            <span style={{ color: '#90a4ae' }}>{f.n} lignes</span>
            <span style={{ color: '#455a64' }}>
              {f.type_contact === 'client' ? 'Clients existants' : f.fournisseur ? `Fournisseur : ${f.fournisseur}` : ''}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: f.obstacle ? '#b71c1c' : '#1b5e20' }}>
              {f.obstacle ? `Envoi bloqué : ${f.obstacle}` : 'En règle'}
            </span>
            <span style={{ flex: 1 }} />
            <button onClick={() => setEdition({ fichier: f.fichier, type_contact: f.type_contact || 'prospect',
                       fournisseur: f.fournisseur || '', consentement: f.consentement || '', date_collecte: f.date_collecte || '' })}
                    style={{ ...champ, background: '#fff', cursor: 'pointer' }}>Renseigner l’origine</button>
            {!f.obstacle && (
              <>
                <input type="number" min="1" max="200" value={lot[f.fichier] || 50}
                       onChange={(e) => setLot({ ...lot, [f.fichier]: e.target.value })}
                       style={{ ...champ, width: 72 }} title="Nombre de mails à envoyer" />
                <button onClick={() => envoyerLot(f)} disabled={!!occupe}
                        style={{ ...champ, background: '#000091', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  {occupe === `lot-${f.fichier}` ? 'Envoi…' : 'Envoyer un lot'}
                </button>
              </>
            )}
          </div>
          {edition && edition.fichier === f.fichier && (
            <div style={{ display: 'grid', gap: 8, marginTop: 10, maxWidth: 640 }}>
              <select value={edition.type_contact} onChange={(e) => setEdition({ ...edition, type_contact: e.target.value })} style={champ}>
                <option value="prospect">Prospects (base achetée)</option>
                <option value="client">Mes clients existants</option>
              </select>
              {edition.type_contact === 'prospect' && (
                <>
                  <input value={edition.fournisseur} onChange={(e) => setEdition({ ...edition, fournisseur: e.target.value })}
                         placeholder="Fournisseur de la base (nom de la société)" style={champ} />
                  <textarea value={edition.consentement} onChange={(e) => setEdition({ ...edition, consentement: e.target.value })}
                            rows={3} style={champ}
                            placeholder="Preuve du consentement : site et formulaire de collecte, texte de la case cochée, référence du contrat ou de l’attestation du fournisseur" />
                  <input value={edition.date_collecte} onChange={(e) => setEdition({ ...edition, date_collecte: e.target.value })}
                         placeholder="Date ou période de collecte (ex. 2025-2026)" style={champ} />
                </>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={enregistrer} disabled={occupe === 'origine'}
                        style={{ ...champ, background: '#000091', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Enregistrer</button>
                <button onClick={() => setEdition(null)} style={{ ...champ, background: '#fff', cursor: 'pointer' }}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DataEsprotect() {
  const [donnees, setDonnees] = useState({ lignes: [], stats: {}, total: 0, pages: 1, fichiers: [] });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [recherchee, setRecherchee] = useState('');
  const [filtre, setFiltre] = useState('');
  const [fichier, setFichier] = useState('');
  const [avec, setAvec] = useState('');          // 'email' | 'telephone'
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState(null);
  useBattement();                       /* le temps restant avance dans le tableau */
  const [enCours, setEnCours] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [selection, setSelection] = useState(null);

  const charger = async ({ silencieux = false } = {}) => {
    if (!silencieux) setChargement(true);
    try {
      const p = new URLSearchParams({ page: String(page), taille: '50' });
      if (recherchee) p.set('q', recherchee);
      if (filtre) p.set('statut', filtre);
      if (fichier) p.set('fichier', fichier);
      if (avec) p.set('avec', avec);
      const r = await fetch(`${API}/esprotect-data?${p}`, { headers: jetonEntete() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setDonnees(j); setErreur(null);
    } catch (e) { setErreur(e.message); }
    if (!silencieux) setChargement(false);
  };
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [page, recherchee, filtre, fichier, avec]);
  useRafraichissementAuto(() => charger({ silencieux: true }), { secondes: 60, actif: !selection });
  useBlocageDefilement(Boolean(selection));

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setRecherchee(recherche.trim()); }, 400);
    return () => clearTimeout(t);
  }, [recherche]);

  /* on prepare le lien des l'ouverture de la fiche, pour qu'il soit affiche */
  useEffect(() => {
    if (selection && !selection.lien) obtenirLien(selection).catch(() => {});
    /* eslint-disable-next-line */
  }, [selection && selection.id]);

  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') setSelection(null); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, []);

  const choisirFichier = (f) => {
    if (!f) return;
    const lecteur = new FileReader();
    lecteur.onload = () => {
      try {
        const lignes = lireCsv(String(lecteur.result));
        if (!lignes.length) throw new Error('aucune ligne lisible dans ce fichier');
        setApercu({ fichier: f.name, lignes });
        setMessage(null);
      } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
    };
    lecteur.readAsText(f, 'utf-8');
  };

  const importer = async (remplacer) => {
    if (!apercu) return;
    setEnCours('import');
    try {
      const r = await fetch(`${API}/esprotect-data/import`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fichier: apercu.fichier, lignes: apercu.lignes, remplacer })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMessage({ type: 'ok',
        texte: `${j.ajoutees} ligne${j.ajoutees > 1 ? 's' : ''} importée${j.ajoutees > 1 ? 's' : ''}`
          + (j.ignorees ? ` · ${j.ignorees} déjà présente${j.ignorees > 1 ? 's' : ''}` : '')
          + (j.opposes ? ` · ${j.opposes} écartée${j.opposes > 1 ? 's' : ''} (liste d’opposition)` : '')
          + '. Pensez à renseigner l’origine du fichier avant tout envoi.' });
      setApercu(null);
      await charger({ silencieux: true });
    } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  /* Le lien court est cree a la premiere utilisation : inutile d'en fabriquer
     210 000 d'avance. On le demande quand on en a besoin, puis il est garde. */
  /* une seule facon d'ecrire la date de fin dans le tableau ET dans la fiche :
     les deux doivent bouger ensemble, sinon le compteur ment a un endroit */
  /* Une confirmation s'efface seule : elle a ete lue ou elle ne l'a pas ete,
     elle n'a pas a rester. Une erreur reste, elle demande une decision. */
  useEffect(() => {
    if (!message || message.type !== 'ok') return undefined;
    const t = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(t);
  }, [message]);

  const poserFenetre = (id, fin) => {
    if (!fin) return;
    setDonnees((d) => ({ ...d,
      lignes: d.lignes.map((x) => (x.id === id ? { ...x, offre_expire_le: fin } : x)) }));
    setSelection((sel) => (sel && sel.id === id ? { ...sel, offre_expire_le: fin } : sel));
  };

  const obtenirLien = async (l) => {
    /* on redemande toujours au serveur : c'est cet appel qui repousse la
       fenetre de 12 heures, meme quand le lien existe deja */
    const r = await fetch(`${API}/esprotect-data/${l.id}/lien`, {
      method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
      body: '{}'
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
    setDonnees((d) => ({ ...d,
      lignes: d.lignes.map((x) => (x.id === l.id ? { ...x, lien: j.lien } : x)) }));
    setSelection((sel) => (sel && sel.id === l.id ? { ...sel, lien: j.lien } : sel));
    poserFenetre(l.id, j.offre_expire_le);
    return j.lien;
  };

  const agir = async (action, l, valeur) => {
    if (action === 'copier') {
      try {
        const lien = await obtenirLien(l);
        if (await copierTexte(lien)) {
          setMessage({ type: 'ok',
            fort: 'Lien copié dans le presse-papier.',
            texte: `Vous pouvez le coller où vous voulez. Le tarif réservé à 89 € est à nouveau `
                 + `valable ${FENETRE_HEURES} heures pour cette personne.` });
        } else {
          /* on ne dit jamais 'copié' quand ça ne l'est pas : on affiche le lien
             pour qu'il puisse être sélectionné à la main */
          setMessage({ type: 'ko', lien,
            fort: 'Le navigateur a refusé la copie.',
            texte: 'Le lien est bien créé et son délai renouvelé : sélectionnez-le ici pour le copier à la main.' });
        }
      } catch (e) { setMessage({ type: 'ko', texte: e.message || 'copie impossible' }); }
      return;
    }
    if (action === 'renouveler') {
      setEnCours(`renouv-${l.id}`);
      try {
        const r = await fetch(`${API}/esprotect-data/${l.id}/renouveler`, {
          method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
          body: '{}'
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
        poserFenetre(l.id, j.offre_expire_le);
        setMessage({ type: 'ok',
          fort: `Délai renouvelé pour ${FENETRE_HEURES} heures.`,
          texte: 'Le tarif réservé à 89 € s’applique de nouveau sur ce lien. '
               + 'Rien n’a été envoyé à cette personne.' });
      } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
      setEnCours(null);
      return;
    }
    if (action === 'envoyer') {
      if (!l.email) { setMessage({ type: 'ko', texte: 'cette ligne n’a pas d’adresse email' }); return; }
      if (!window.confirm(`Envoyer le mail d’alerte ES-Protect à ${l.email} ?`)) return;
      setEnCours(`envoi-${l.id}`);
      try {
        const r = await fetch(`${API}/esprotect-data/${l.id}/lien`, {
          method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ envoyer: true })
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
        poserFenetre(l.id, j.offre_expire_le);
        setMessage({ type: 'ok',
          fort: `Lien envoyé à ${l.email}.`,
          texte: `Le tarif réservé à 89 € est valable ${FENETRE_HEURES} heures à compter de maintenant.` });
        setSelection(null);
        await charger({ silencieux: true });
      } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
      setEnCours(null);
      return;
    }
    if (action === 'statut') {
      /* on montre le nouveau statut immediatement : attendre le serveur pour
         voir la pastille changer donne l'impression que le clic n'a pas pris */
      setDonnees((d) => ({ ...d,
        lignes: d.lignes.map((x) => (x.id === l.id ? { ...x, statut: valeur } : x)) }));
      setSelection((sel) => (sel && sel.id === l.id ? { ...sel, statut: valeur } : sel));
      try {
        const r = await fetch(`${API}/esprotect-data/${l.id}/statut`, {
          method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ statut: valeur })
        });
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
      } catch (e) {
        setMessage({ type: 'ko', texte: `statut non enregistré : ${e.message}` });
      }
      await charger({ silencieux: true });   /* met les compteurs a jour */
      return;
    }
    if (action === 'supprimer') {
      if (!window.confirm('Supprimer cette ligne du fichier ?')) return;
      await fetch(`${API}/esprotect-data/${l.id}`, { method: 'DELETE', headers: jetonEntete() });
      setSelection(null);
      await charger({ silencieux: true });
    }
  };

  const s = donnees.stats || {};

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '22px 24px 28px',
                  boxShadow: '0 4px 18px rgba(0,0,0,.08)' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '0 0 4px',
                   fontSize: 20, color: '#263238' }}>
        <Database size={20} color="#000091" /> Data ES-PROTECT
      </h2>
      <p style={{ margin: '0 0 18px', fontSize: 12.5, color: '#90a4ae' }}>
        Votre fichier de prospects. Cliquez sur une ligne pour voir toute sa fiche. Chaque
        personne peut recevoir un lien d’ouverture de dossier <b>déjà rempli</b> : elle n’a plus
        qu’à accepter le mandat et à saisir sa carte.
      </p>

      <div style={{ border: '1px dashed #c5cbd8', borderRadius: 10, padding: '14px 18px',
                    background: '#fbfcfe', marginBottom: 18 }}
           onDragOver={(e) => e.preventDefault()}
           onDrop={(e) => { e.preventDefault(); choisirFichier(e.dataTransfer.files[0]); }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Upload size={18} color="#667eea" />
          <div style={{ flex: 1, minWidth: 220 }}>
            <b style={{ fontSize: 13.5, color: '#263238' }}>Ajouter un fichier</b>
            <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#78909c' }}>
              Glissez un CSV ici. Les colonnes sont reconnues toutes seules.
            </p>
          </div>
          <input type="file" accept=".csv,.txt,.tsv,text/csv"
                 onChange={(e) => choisirFichier(e.target.files[0])} style={{ fontSize: 12.5 }} />
        </div>
        {apercu && (
          <div style={{ marginTop: 12, borderTop: '1px solid #e8ecf3', paddingTop: 12 }}>
            <b style={{ fontSize: 13, color: '#263238' }}>{apercu.fichier}</b>
            <span style={{ color: '#78909c', fontSize: 12.5 }}> · {nombre(apercu.lignes.length)} lignes
              · colonnes : {Object.keys(apercu.lignes[0]).join(', ')}</span>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={() => importer(false)} disabled={enCours === 'import'}
                      style={{ ...champ, background: '#000091', color: '#fff', border: 'none',
                               cursor: 'pointer', fontWeight: 600 }}>
                {enCours === 'import' ? 'Import…' : 'Ajouter au fichier existant'}
              </button>
              <button onClick={() => { if (window.confirm('Remplacer tout le contenu actuel ?')) importer(true); }}
                      disabled={enCours === 'import'} style={{ ...champ, background: '#fff', cursor: 'pointer' }}>
                Remplacer tout
              </button>
              <button onClick={() => setApercu(null)}
                      style={{ ...champ, background: '#fff', cursor: 'pointer', color: '#78909c' }}>
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <OrigineFichiers key={donnees.total} jetonEntete={jetonEntete} champ={champ} onMessage={setMessage}
                       onEnvoye={() => charger({ silencieux: true })} />

      {/* Au-dessus de la fiche (z-index 1000) : une confirmation invisible ne
          confirme rien, et c'est justement depuis la fiche qu'on copie. */}
      {message && (
        <div style={{ padding: '11px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: message.type === 'ok' ? '#e8f5e9' : '#ffebee',
                      color: message.type === 'ok' ? '#1b5e20' : '#b71c1c',
                      position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
                      zIndex: 1100, width: 'min(680px, calc(100% - 32px))',
                      boxShadow: '0 10px 32px rgba(10,15,30,.22)',
                      border: `1px solid ${message.type === 'ok' ? '#a5d6a7' : '#ef9a9a'}` }}>
          {message.type === 'ok' ? <Check size={16} /> : <AlertTriangle size={16} />}
          <span style={{ flex: 1, lineHeight: 1.5 }}>
            {message.fort && <b style={{ display: 'block', fontSize: 14 }}>{message.fort}</b>}
            {message.texte}
            {message.type === 'ko' && message.lien && (
              <input readOnly value={message.lien}
                     onFocus={(ev) => ev.target.select()}
                     onClick={(ev) => ev.target.select()}
                     style={{ display: 'block', width: '100%', marginTop: 6, padding: '6px 8px',
                              border: '1px solid #ef9a9a', borderRadius: 6, fontSize: 12,
                              background: '#fff', color: '#b71c1c' }} />
            )}
          </span>
          <button onClick={() => setMessage(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <X size={15} />
          </button>
        </div>
      )}

      {/* chaque compteur est un filtre : un clic reduit le tableau a ces lignes,
          un second clic revient a tout le fichier */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          { t: 'Lignes', v: s.total, c: '#263238', actif: !filtre && !avec,
            au: () => { setPage(1); setFiltre(''); setAvec(''); } },
          { t: 'Avec email', v: s.avec_email, c: '#667eea', actif: avec === 'email',
            au: () => { setPage(1); setFiltre(''); setAvec(avec === 'email' ? '' : 'email'); } },
          { t: 'Avec téléphone', v: s.avec_telephone, c: '#764ba2', actif: avec === 'telephone',
            au: () => { setPage(1); setFiltre(''); setAvec(avec === 'telephone' ? '' : 'telephone'); } },
          /* demandes de rappel venues du site : consentement a l'appel prouve */
          { t: 'À rappeler', v: s.a_rappeler, c: '#b45309', actif: filtre === 'a_rappeler',
            au: () => { setPage(1); setAvec(''); setFiltre(filtre === 'a_rappeler' ? '' : 'a_rappeler'); } },
          { t: 'À contacter', v: s.a_contacter, c: '#455a64', actif: filtre === 'a_contacter',
            au: () => { setPage(1); setAvec(''); setFiltre(filtre === 'a_contacter' ? '' : 'a_contacter'); } },
          { t: 'Liens envoyés', v: s.lien_envoye, c: '#14448f', actif: filtre === 'lien_envoye',
            au: () => { setPage(1); setAvec(''); setFiltre(filtre === 'lien_envoye' ? '' : 'lien_envoye'); } },
          { t: 'Inscrits', v: s.inscrit, c: '#1b5e20', actif: filtre === 'inscrit',
            au: () => { setPage(1); setAvec(''); setFiltre(filtre === 'inscrit' ? '' : 'inscrit'); } }
        ].map((k) => (
          <button key={k.t} onClick={k.au}
                  title={k.actif ? 'Cliquez pour revenir à tout le fichier'
                                 : `N’afficher que : ${k.t.toLowerCase()}`}
                  style={{ border: `1px solid ${k.actif ? k.c : '#eceff1'}`, borderRadius: 8,
                           padding: '10px 16px', minWidth: 108, cursor: 'pointer', textAlign: 'left',
                           background: k.actif ? `${k.c}0f` : '#fff',
                           boxShadow: k.actif ? `inset 0 -2px 0 ${k.c}` : 'none',
                           transition: 'border-color .15s, background .15s' }}>
            <div style={{ fontSize: 19, fontWeight: 700, color: k.c }}>{nombre(k.v)}</div>
            <div style={{ fontSize: 11.5, color: k.actif ? k.c : '#90a4ae' }}>{k.t}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#b0bec5' }} />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
                 placeholder="Nom, email, téléphone, ville…"
                 style={{ ...champ, width: '100%', paddingLeft: 34 }} />
        </div>
        <select value={filtre} onChange={(e) => { setPage(1); setAvec(''); setFiltre(e.target.value); }} style={champ}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.texte}</option>)}
        </select>
        <select value={fichier} onChange={(e) => { setPage(1); setFichier(e.target.value); }} style={champ}>
          <option value="">Tous les fichiers</option>
          {(donnees.fichiers || []).map((f) => (
            <option key={f.fichier || 'sans'} value={f.fichier || ''}>
              {f.fichier || 'sans fichier'} ({nombre(f.n)})
            </option>
          ))}
        </select>
        <button onClick={() => charger({ silencieux: true })}
                style={{ ...champ, background: '#fff', cursor: 'pointer', display: 'flex',
                         alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                               borderRadius: 6, marginBottom: 14 }}>{erreur}</div>}

      {chargement ? <p style={{ color: '#90a4ae' }}>Chargement…</p> : donnees.lignes.length === 0 ? (
        <p style={{ color: '#90a4ae', fontSize: 13.5 }}>
          {donnees.total === 0 && !recherchee && !avec
            ? 'Aucune donnée pour le moment : importez votre fichier ci-dessus.'
            : 'Aucune ligne ne correspond à cette recherche.'}
        </p>
      ) : (
        <div style={{ border: '1px solid #eceff1', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13,
                          tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '21%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '23%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f7f8fb', textAlign: 'left' }}>
                {['Personne', 'Téléphone', 'Adresse email', 'Où', 'Statut', ''].map((t, i) => (
                  <th key={i} style={{ padding: '10px 12px', fontSize: 11.5, color: '#78909c',
                                       textTransform: 'uppercase', letterSpacing: '.04em',
                                       textAlign: i === 5 ? 'right' : 'left' }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {donnees.lignes.map((l, i) => {
                const st = STATUTS[l.statut] || STATUTS.a_contacter;
                const tronque = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                return (
                  <tr key={l.id} onClick={() => setSelection(l)}
                      style={{ cursor: 'pointer', background: i % 2 ? '#fcfcfd' : '#fff',
                               borderBottom: '1px solid #f3f5f8' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f1fb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 ? '#fcfcfd' : '#fff'; }}>
                    <td style={{ padding: '10px 12px', ...tronque }}>
                      <b style={{ color: '#263238' }}>
                        {[l.prenom, l.nom].filter(Boolean).join(' ') || '—'}</b>
                      <div style={{ color: '#78909c', fontSize: 12, ...tronque }}>
                        {l.date_naissance ? `né(e) le ${l.date_naissance}` : l.fichier}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#263238', fontSize: 13.5,
                                 fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {l.telephone || <span style={{ color: '#b0bec5', fontWeight: 400 }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', ...tronque, fontSize: 13,
                                 color: l.email ? '#1a237e' : '#c62828' }}
                        title={l.email || 'aucune adresse email sur cette ligne'}>
                      {l.email || 'pas d’email'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#546e7a', ...tronque }}
                        title={l.adresse || ''}>
                      {(l.donnees && (l.donnees.Ville || l.donnees.ville)) || l.adresse || '—'}
                      <div style={{ color: '#90a4ae', fontSize: 12 }}>
                        {(l.donnees && (l.donnees.CP || l.donnees.cp)) || ''}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }} onClick={(e) => e.stopPropagation()}>
                      <select value={l.statut} onChange={(e) => agir('statut', l, e.target.value)}
                              title="Changer le statut sans ouvrir la fiche"
                              style={{ background: st.fond, color: st.couleur, borderRadius: 999,
                                       padding: '5px 8px', fontSize: 12, fontWeight: 600,
                                       border: `1px solid ${st.couleur}22`, cursor: 'pointer',
                                       width: '100%', appearance: 'none', textAlign: 'center' }}>
                        {Object.entries(STATUTS).map(([k, v]) => (
                          <option key={k} value={k} style={{ background: '#fff', color: '#263238' }}>
                            {v.texte}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const f = etatFenetre(l.offre_expire_le);
                        if (!f.connue) return null;
                        return (
                          <div title={f.ouverte
                                ? `Tarif réservé à 89 € encore valable ${f.texte}`
                                : 'La fenêtre de 12 h est fermée : ce lien affiche le tarif public'}
                               style={{ marginTop: 4, textAlign: 'center', fontSize: 11.5,
                                        fontWeight: 600, whiteSpace: 'nowrap',
                                        color: f.ouverte ? '#2e7d32' : '#b26a00' }}>
                            {f.ouverte ? `89 € · ${f.texte}` : '89 € · expiré'}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => agir('envoyer', l)}
                              disabled={!l.email || enCours === `envoi-${l.id}`}
                              title={l.email ? 'Envoyer le lien prérempli par email'
                                             : 'Pas d’adresse email sur cette ligne'}
                              style={{ padding: '6px 10px', border: 'none', borderRadius: 6,
                                       background: l.email ? '#000091' : '#cfd8dc', color: '#fff',
                                       cursor: l.email ? 'pointer' : 'not-allowed', fontSize: 12.5,
                                       fontWeight: 600, marginRight: 6 }}>
                        <Send size={13} style={{ verticalAlign: '-2px' }} />
                      </button>
                      <button onClick={() => agir('copier', l)} title="Copier le lien"
                              style={{ padding: '6px 9px', border: '1px solid #dfe3ea',
                                       borderRadius: 6, background: '#fff', cursor: 'pointer',
                                       marginRight: 6 }}>
                        <Link2 size={13} style={{ verticalAlign: '-2px' }} />
                      </button>
                      <button onClick={() => setSelection(l)} title="Voir la fiche complète"
                              style={{ padding: '6px 9px', border: '1px solid #dfe3ea',
                                       borderRadius: 6, background: '#fff', cursor: 'pointer',
                                       color: '#3949ab' }}>
                        <ChevronRight size={13} style={{ verticalAlign: '-2px' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {donnees.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
                      flexWrap: 'wrap', fontSize: 13, color: '#546e7a' }}>
          <span>
            {nombre(donnees.total)} ligne{donnees.total > 1 ? 's' : ''}
            {recherchee || filtre || fichier || avec ? ' pour ce filtre' : ''} · page {donnees.page} sur {nombre(donnees.pages)}
          </span>
          <span style={{ flex: 1 }} />
          <button onClick={() => setPage(1)} disabled={page <= 1}
                  style={{ ...champ, background: '#fff', cursor: page <= 1 ? 'default' : 'pointer' }}>
            Début
          </button>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                  style={{ ...champ, background: '#fff', cursor: page <= 1 ? 'default' : 'pointer' }}>
            Précédent
          </button>
          <button onClick={() => setPage((p) => Math.min(donnees.pages, p + 1))}
                  disabled={page >= donnees.pages}
                  style={{ ...champ, background: '#fff',
                           cursor: page >= donnees.pages ? 'default' : 'pointer' }}>
            Suivant
          </button>
        </div>
      )}

      {selection && (
        <Fiche ligne={selection} onFermer={() => setSelection(null)}
               onAction={agir} enCours={enCours} />
      )}
    </div>
  );
}
